import { createContext, use } from 'react'

import { UserRole, type IUser } from '@/types/user'

export interface AuthContextValue {
    user: IUser | null
    /** True once the session probe has settled either way. */
    isReady: boolean
    isAuthenticated: boolean
    /**
     * The probe failed with 401 `{ requires2fa: true }`. A session exists, but
     * this network has not entered a code. Distinct from being signed out.
     */
    requires2fa: boolean
    /** Destroys the session server-side, then clears the local caches. */
    signOut: () => Promise<void>
}

/**
 * The only session the console lets in: an admin with 2FA turned on.
 * `AuthGate` and `/login` both use this, so one cannot redirect the operator
 * back to the page the other just sent them away from.
 */
export function isAdmitted(user: IUser | null | undefined, isAuthenticated: boolean): boolean {
    return isAuthenticated && user?.role === UserRole.Admin && user.two_factor?.enabled === true
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
    const context = use(AuthContext)
    if (!context) throw new Error('useAuth must be used within an <AuthProvider>')
    return context
}

/**
 * The signed-in user, guaranteed non-null. Only valid below the auth gate, which
 * will not render children until a session exists -- so components inside it do
 * not need to null-check on every access.
 */
export function useCurrentUser(): IUser {
    const { user } = useAuth()
    if (!user) throw new Error('useCurrentUser called outside an authenticated route')
    return user
}
